import {
  Labeled,
  SimpleShowLayout,
  useGetList,
  useRecordContext,
  useRefresh,
} from 'react-admin';
import CheckIcon from '@mui/icons-material/Check';
import HistoryIcon from '@mui/icons-material/History';
import { Box, Typography, Chip } from '@mui/material';
import { useMemo } from 'react';
import { ForceAggregationButton } from '../therapistRecordings/ForceAggregationButton';
import { CopiableField } from '@/components/CopiableIdField';
import { RawDataButton } from '@/components/RawDataButton';
import EditIcon from '@mui/icons-material/Edit';
import MicIcon from '@mui/icons-material/Mic';
import CreateIcon from '@mui/icons-material/Create';
import VerifiedIcon from '@mui/icons-material/Verified';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import TextsmsIcon from '@mui/icons-material/Textsms';
import ErrorIcon from '@mui/icons-material/Error';
import CallSplitIcon from '@mui/icons-material/CallSplit';
// import { useQueryClient } from "@tanstack/react-query";
import { formatAudioDuration } from '@/components/util';
import { RefreshIconButton } from '@/components/util/RefreshIconButton';
import { TherapistRecordingsAccordion } from './TherapistRecordingsAccordion';
import { ForceProcessingButton } from './ForceProcessingButton';
import { TherapistInputSourcesAccordion } from './TherapistInputSourcesAccordion';
import { DeadLetterQueueButton } from '@/components/DeadLetterQueueButton';
import { HardwareInfoButton } from '@/components/HardwareInfoButton';
import { IngestionTrackerButton } from '@/components/IngestionTrackerButton';

export const TherapistSessionNotesCommandPanel = () => {
  const sessionNote = useRecordContext();

  const collectionName = useMemo(() => {
    if (sessionNote.deletedAt) {
      return 'therapistRecordings_deleted';
    }

    if (sessionNote.status === 'processed') {
      return 'therapistRecordings_ready';
    }

    return 'therapistRecordings';
  }, [sessionNote?.deletedAt, sessionNote?.status]);

  const {
    data: recordings,
    isLoading,
    isFetching,
    refetch,
  } = useGetList(collectionName, {
    filter: { sessionNoteId: { $oid: sessionNote.id } },
    sort: { field: '_id', order: 'DESC' },
    pagination: { page: 1, perPage: 1000 },
    meta: {
      projection: {
        transcript: {
          $binarySize: '$transcript',
        },
        uploadedAudioContentSlices: {
          $sum: {
            $map: {
              input: '$uploadedAudioContentSlices',
              as: 'slice',
              in: {
                $binarySize: '$$slice',
              },
            },
          },
        },
        operationHistory: {
          $concatArrays: [
            '$operationHistory',
            '$serverOperationHistory',
          ],
        },
      },
    },
  });

  const lastUpdatedAt = useMemo(() => {
    const [firstRecording] = recordings || [];

    return (
      sessionNote.lastUpdatedAt ||
      firstRecording?.lastUpdatedAt ||
      firstRecording?.createdAt
    );
  }, [sessionNote, recordings]);

  const recordingStatusCounts = useMemo(() => {
    if (!recordings) {
      return {};
    }

    return recordings.reduce((acc, recording) => {
      const status = recording.status || 'unknown';
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    }, {});
  }, [recordings]);

  const statusConfig = useMemo(
    () => ({
      draft: {
        icon: <CreateIcon />,
        color: '#FFA726',
        label: 'Draft',
      },
      committed: {
        icon: <CheckIcon />,
        color: '#42A5F5',
        label: 'Committed',
      },
      splitted: {
        icon: <CallSplitIcon />,
        color: '#1976D2',
        label: 'Splitted',
      },
      validated: {
        icon: <VerifiedIcon />,
        color: '#66BB6A',
        label: 'Validated',
      },
      uploaded: {
        icon: <CloudUploadIcon />,
        color: '#7E57C2',
        label: 'Uploaded',
      },
      transcribed: {
        icon: <TextsmsIcon />,
        color: '#b61ee8',
        label: 'Transcribed',
      },
      failed: {
        icon: <ErrorIcon />,
        color: '#F44336',
        label: 'Failed',
      },
    }),
    [],
  );

  const lastUpdatedAtChipProps = useMemo(
    () => ({
      icon: <HistoryIcon />,
      // color: "info",
    }),
    [],
  );

  const refreshAll = useRefresh();

  return (
    <SimpleShowLayout record={sessionNote}>
      <h2 style={{ display: 'flex', alignItems: 'center' }}>
        Metadata
        <Box marginLeft="1rem">
          <RefreshIconButton isFetching={isFetching} />
          <ForceAggregationButton
            sessionNote={sessionNote}
            recordings={recordings || []}
          />
          <ForceProcessingButton sessionNote={sessionNote} />
          <HardwareInfoButton info={sessionNote.deviceInfo} />
          <IngestionTrackerButton
            ingestionRecords={sessionNote.ingestionTracker}
          />
          <RawDataButton
            entity={sessionNote}
            title={`Raw Session Note Data: #${sessionNote.id}`}
          />
          <DeadLetterQueueButton
            documentId={sessionNote.id as string}
            userId={sessionNote.therapistId}
            collectionName="therapistSessionNotes"
          />
        </Box>
      </h2>
      <Box display="flex">
        <Labeled label="Note Id">
          <CopiableField
            targetNavigationResource="therapistSessionNotes"
            targetNavigationFilters={{
              _id: sessionNote.id,
              isHiddenTestAccount: 'visible',
            }}
            content={sessionNote.id as any}
            copyLink
          />
        </Labeled>
        {sessionNote.deviceInfo?.timezone && (
          <Labeled marginLeft="1rem" label="Timezone">
            <Chip
              label={`${sessionNote.deviceInfo?.timezone?.name} (${sessionNote.deviceInfo?.timezone?.gmt})`}
            />
          </Labeled>
        )}
        <Labeled marginLeft="1rem" label="Status">
          <Typography>{sessionNote.status}</Typography>
        </Labeled>
        <Labeled marginLeft="1rem" label="Media Type">
          <Chip
            icon={
              sessionNote.mediaType === 'text' ? (
                <EditIcon />
              ) : sessionNote.mediaType === 'audio' ? (
                <MicIcon />
              ) : undefined
            }
            color={
              sessionNote.mediaType === 'text'
                ? 'success'
                : sessionNote.mediaType === 'audio'
                  ? 'info'
                  : 'default'
            }
            size="small"
            sx={{
              color: 'white',
              padding: '12px',
              backgroundColor:
                sessionNote.mediaType === 'text'
                  ? '#4caf50'
                  : undefined,
              '& .MuiChip-icon': {
                color: 'white',
                margin: 0,
                fontSize: '1.2rem',
              },
              '& .MuiChip-label': {
                display: 'none',
              },
            }}
          />
        </Labeled>
        {sessionNote.mediaType === 'audio' && (
          <Labeled marginLeft="1rem" label="Total Audio Duration">
            <Typography>
              {formatAudioDuration(
                sessionNote.totalAudioDuration || 0,
              )}
            </Typography>
          </Labeled>
        )}
        <Labeled marginLeft="1rem" label="Aggregated Transcript">
          <Typography>
            {!!sessionNote?.transcript
              ? `${sessionNote.wordCount?.transcript} words`
              : '⚠️ missing'}
          </Typography>
        </Labeled>
        {!!sessionNote?.transcript && !!sessionNote?.wordCount && (
          <Labeled marginLeft="1rem" label="Word Count">
            <Typography>
              {sessionNote.wordCount?.transcript}
            </Typography>
          </Labeled>
        )}
        <Labeled marginLeft="1rem" label="Therapist Id">
          <CopiableField
            targetNavigationResource="therapists"
            targetNavigationFilters={{
              _id: sessionNote.therapistId,
              isHiddenTestAccount: 'visible',
            }}
            content={sessionNote.therapistId}
            filter={{
              therapistId: sessionNote.therapistId,
            }}
            copyLink
          />
        </Labeled>
        <Labeled marginLeft="1rem" label="Patient Id">
          <CopiableField
            targetNavigationResource="therapistPatients"
            targetNavigationFilters={{
              _id: { $oid: sessionNote.patientId },
            }}
            content={sessionNote.patientId}
          />
        </Labeled>
        <Labeled marginLeft="1rem" label="App Fingerprint">
          <CopiableField content={sessionNote.appFingerprint} />
        </Labeled>
      </Box>
      {sessionNote.lastErrorMessage && (
        <Box
          display="flex"
          minWidth="30%"
          maxWidth="50%"
          marginLeft="1rem"
        >
          <Labeled label="Last Error Message" color="#f99">
            <Typography color="#f99">
              {sessionNote.lastErrorMessage}
            </Typography>
          </Labeled>
        </Box>
      )}
      {!sessionNote.inputSources?.length && (
        <TherapistRecordingsAccordion
          sessionNote={sessionNote}
          recordings={recordings || []}
          isLoading={isLoading}
          isFetching={isFetching}
          refetch={refetch}
        />
      )}
      {!!sessionNote?.inputSources?.length && (
        <TherapistInputSourcesAccordion
          inputSources={sessionNote?.inputSources}
          recordings={recordings || []}
          isFetching={isFetching}
        />
      )}
    </SimpleShowLayout>
  );
};
